import React, { useState, useMemo } from "react";
import axiosInstance from "../components/api/axiosInstance";
import BusinessLeadsTable from "./BusinessLeadsTable";

/**
 * Shared API helper for searching business leads.
 * Used by both this page component and the DigixLeads container.
 */
export async function searchBusinessLeadsApi(filters, servicesData, allCities) {
  const params = new URLSearchParams();

  // Combined name/phone search
  const nameOrPhoneValue =
    (filters.nameOrPhone || filters.name || filters.phone || "").trim();
  if (nameOrPhoneValue) {
    const digitsOnly = nameOrPhoneValue.replace(/\D/g, "");
    const hasDigits = digitsOnly.length > 0;
    if (hasDigits) {
      params.append("phone", nameOrPhoneValue);
    } else {
      params.append("name", nameOrPhoneValue);
    }
  }

  // Status filter
  if (filters.status && filters.status !== "All Statuses") {
    params.append("status", filters.status);
  }

  // Service filter -> map to service_id using servicesData
  if (filters.service && filters.service !== "All Services") {
    for (const category of servicesData || []) {
      if (category.subcategories && Array.isArray(category.subcategories)) {
        const subcategory = category.subcategories.find(
          (sub) => sub.subcategory_name === filters.service
        );
        if (subcategory && subcategory.subcategory_id) {
          params.append("service_id", subcategory.subcategory_id);
          break;
        }
      }
    }
  }

  // City filter -> map to city_id using allCities
  if (filters.city) {
    const cityObj = (allCities || []).find(
      (c) =>
        c.city_name?.toLowerCase() === filters.city.toLowerCase() ||
        c.City_Name?.toLowerCase() === filters.city.toLowerCase()
    );
    if (cityObj) {
      const cityId = cityObj.city_id || cityObj.City_Id;
      if (cityId) {
        params.append("city_id", cityId);
      }
    }
  }

  // Source filter
  if (filters.sourceId) {
    params.append("source_id", filters.sourceId);
  }

  if (filters.nextFollowupDate) {
    const dateValue = filters.nextFollowupDate.trim();
    if (dateValue) {
      params.append("next_followup_date", dateValue);
    }
  }

  const response = await axiosInstance.get(
    `/search-leads?${params.toString()}`
  );

  if (!response.data || !response.data.results) {
    return [];
  }

  return response.data.results.map((item) => {
    const lead = item.lead;
    const address = item.address || {};
    const followups = item.followups || [];
    // API returns followups ordered by Created_On desc — first is latest
    const latestFollowup = followups.length > 0 ? followups[0] : null;

    let nextFollowupDateStr = "—";
    let lastFollowupDateStr = "—";
    let lastFollowupNotesStr = "—";

    if (latestFollowup) {
      if (latestFollowup.Next_Followup_Date) {
        const date = new Date(latestFollowup.Next_Followup_Date);
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          nextFollowupDateStr = `${day}/${month}/${year}`;
        }
      }
      if (latestFollowup.Followup_Date) {
        const date = new Date(latestFollowup.Followup_Date);
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          lastFollowupDateStr = `${day}/${month}/${year}`;
        }
      }
      if (latestFollowup.Remarks) {
        lastFollowupNotesStr = latestFollowup.Remarks;
      }
    }

    return {
      id: lead.Lead_Id,
      name: `${lead.First_Name || ""} ${lead.Last_Name || ""}`.trim(),
      email: lead.Email || "—",
      phone: lead.Phone || "—",
      countryCode: lead.Country_Code || lead.country_code || "+91",
      companyName: lead.Company_Name || "—",
      domainId: lead.Domain_Id ?? null,
      domain: lead.Domain || "—",
      companyEmail: lead.Company_Email || "—",
      companyPhone: lead.Company_Phone || "—",
      location: address.City_Name || "—",
      services: lead.Services || [],
      status: lead.Status || "—",
      statusId: lead.Status_Id ?? lead.status_id ?? null,
      source: lead.Source_Name || "—",
      createdAt: lead.Created_On
        ? new Date(lead.Created_On).toLocaleDateString("en-GB")
        : "—",
      assignedToId: lead.Assigned_To ?? null,
      assignedTo:
        lead.Assigned_To_Name ||
        (lead.Assigned_To != null ? `User #${lead.Assigned_To}` : "—"),
      lastFollowupDate: lastFollowupDateStr,
      notes: lastFollowupNotesStr,
      nextFollowupDate: nextFollowupDateStr,
      isQuoteSent: lead.Is_Quote_Sent ? "Yes" : "No",
      tenantId: lead.Tenant_Id ?? null,
      tenantName: lead.Tenant_Name || "",
      tenantStatus: lead.Tenant_Status || "",
      tenantDomainId: lead.Tenant_Domain_Id ?? null,
    };
  });
}

/**
 * BusinessLeadsPage
 * 
 * Encapsulates all filter state, search logic, free search, and pagination
 * for the Business Leads tab. It receives the base `leads` list and shared
 * metadata (services, servicesData, cities, domains) from the parent.
 */
function BusinessLeadsPage({
  leads = [],
  services = [],
  servicesData = [],
  allCities = [],
  domains = [],
  rowsPerPage = 5,
  onViewLead,
  onEditLead,
}) {
  const [filters, setFilters] = useState({
    name: "",
    phone: "",
    nameOrPhone: "",
    city: "",
    domain: "",
    service: "All Services",
    status: "All Statuses",
    nextFollowupDate: "",
    sourceId: "",
    freeSearch: "",
  });

  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Reset to first page when filters change
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      name: "",
      phone: "",
      nameOrPhone: "",
      city: "",
      domain: "",
      service: "All Services",
      status: "All Statuses",
      nextFollowupDate: "",
      sourceId: "",
      freeSearch: "",
    });
    setSearchResults(null);
    setIsSearching(false);
    setCurrentPage(1);
  };

  // Filters that should go to the backend `/search-leads` API.
  // Domain is intentionally excluded here because the API does not
  // currently support domain filtering; we handle that purely
  // on the client side in `filteredLeads`.
  const hasSearchFilters = () => {
    return (
      filters.name ||
      filters.phone ||
      filters.nameOrPhone ||
      filters.city ||
      (filters.service && filters.service !== "All Services") ||
      (filters.status && filters.status !== "All Statuses") ||
      filters.nextFollowupDate ||
      filters.sourceId
    );
  };

  const searchBusinessLeads = async () => {
    setIsSearching(true);
    try {
      const results = await searchBusinessLeadsApi(
        filters,
        servicesData,
        allCities
      );
      setSearchResults(results);
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.message ||
        "Error searching business leads.";
      console.error("Error searching business leads:", msg);
      setSearchResults([]);
      alert(
        "Error searching business leads: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchClick = () => {
    if (hasSearchFilters()) {
      searchBusinessLeads();
    } else {
      setSearchResults(null);
      setIsSearching(false);
    }
  };

  // Base dataset: either API search results or the original leads
  const baseLeads = useMemo(
    () => (searchResults !== null ? searchResults : leads),
    [searchResults, leads]
  );

  // Client-side filters (in addition to API filters)
  const filteredLeads = useMemo(() => {
    return baseLeads.filter((lead) => {
      // Combined name/phone filter (when not using API)
      if (filters.nameOrPhone && searchResults === null) {
        const searchValue = filters.nameOrPhone.toLowerCase().trim();
        const nameMatch = lead.name?.toLowerCase().includes(searchValue);
        const phoneMatch = lead.phone?.includes(filters.nameOrPhone.trim());
        if (!nameMatch && !phoneMatch) return false;
      }

      if (
        filters.name &&
        !lead.name?.toLowerCase().includes(filters.name.toLowerCase())
      ) {
        return false;
      }
      if (filters.phone && !lead.phone?.includes(filters.phone)) {
        return false;
      }
      if (
        filters.city &&
        !lead.location?.toLowerCase().includes(filters.city.toLowerCase())
      ) {
        return false;
      }
      if (filters.domain) {
        const selectedDomain = filters.domain.toLowerCase();

        // Direct domain name on the row (may already be resolved)
        const leadDomainName = (lead.domain || "").toLowerCase();

        // Fallback: resolve from Domain_Id using the provided domains list
        let leadDomainFromId = "";
        if (
          lead.domainId &&
          Array.isArray(domains) &&
          domains.length > 0
        ) {
          const dom = domains.find(
            (d) => d.id === lead.domainId || String(d.id) === String(lead.domainId)
          );
          if (dom && dom.name) {
            leadDomainFromId = String(dom.name).toLowerCase();
          }
        }

        if (
          !leadDomainName.includes(selectedDomain) &&
          !leadDomainFromId.includes(selectedDomain)
        ) {
          return false;
        }
      }
      if (
        filters.service &&
        filters.service !== "All Services" &&
        !lead.services?.includes(filters.service)
      ) {
        return false;
      }
      if (filters.status && filters.status !== "All Statuses") {
        const leadStatus = String(lead.status || "").trim();
        const filterStatus = String(filters.status || "").trim();
        if (leadStatus.toLowerCase() !== filterStatus.toLowerCase())
          return false;
      }
      if (filters.nextFollowupDate) {
        const filterDate = filters.nextFollowupDate; // YYYY-MM-DD
        if (!lead.nextFollowupDate || lead.nextFollowupDate === "—") {
          return false;
        }
        const datePart = lead.nextFollowupDate.split(" ")[0] || "";
        let leadDateISO = "";
        if (datePart.includes("-") && datePart.length >= 10) {
          leadDateISO = datePart.substring(0, 10);
        } else {
          const [d, m, y] = datePart.split("/");
          if (y && m && d)
            leadDateISO = `${y}-${m.padStart(2, "0")}-${d.padStart(
              2,
              "0"
            )}`;
        }
        if (leadDateISO !== filterDate) return false;
      }
      return true;
    });
  }, [baseLeads, filters, searchResults]);

  // Free search across all columns
  const freeSearchFilteredLeads = useMemo(() => {
    if (!filters.freeSearch) return filteredLeads;
    const searchValue = filters.freeSearch.toLowerCase().trim();
    if (!searchValue) return filteredLeads;

    return filteredLeads.filter((lead) => {
      const name = (lead.name || "").toLowerCase();
      const email = (lead.email || "").toLowerCase();
      const phone = (lead.phone || "").toLowerCase();
      const location = (lead.location || "").toLowerCase();
      const createdAt = (lead.createdAt || "").toLowerCase();
      const lastFollowupDate = (lead.lastFollowupDate || "").toLowerCase();
      const notes = (lead.notes || "").toLowerCase();
      const nextFollowupDate = (lead.nextFollowupDate || "").toLowerCase();
      const status = (lead.status || "").toLowerCase();
      const source = (lead.source || "").toLowerCase();
      const isQuoteSent = (lead.isQuoteSent || "")
        .toString()
        .toLowerCase();
      const companyName = (lead.companyName || "").toLowerCase();
      const domain = (lead.domain || "").toLowerCase();
      const servicesStr = Array.isArray(lead.services)
        ? lead.services.join(", ").toLowerCase()
        : (lead.services || "").toLowerCase();

      return (
        name.includes(searchValue) ||
        email.includes(searchValue) ||
        phone.includes(searchValue) ||
        location.includes(searchValue) ||
        createdAt.includes(searchValue) ||
        lastFollowupDate.includes(searchValue) ||
        notes.includes(searchValue) ||
        nextFollowupDate.includes(searchValue) ||
        status.includes(searchValue) ||
        source.includes(searchValue) ||
        isQuoteSent.includes(searchValue) ||
        companyName.includes(searchValue) ||
        domain.includes(searchValue) ||
        servicesStr.includes(searchValue)
      );
    });
  }, [filteredLeads, filters.freeSearch]);

  // Pagination
  const totalPages = Math.ceil(freeSearchFilteredLeads.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentLeads = freeSearchFilteredLeads.slice(startIndex, endIndex);
  const showPagination = freeSearchFilteredLeads.length >= rowsPerPage;
  const startRow = freeSearchFilteredLeads.length > 0 ? startIndex + 1 : 0;
  const endRow = Math.min(endIndex, freeSearchFilteredLeads.length);
  const totalRows = freeSearchFilteredLeads.length;

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, "...", totalPages];
    }
    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  return (
    <div className="business-leads-page">
      {/* Filters row for business leads */}
      <div className="search-filters-row">
        <input
          type="text"
          className="search-filter-input"
          placeholder="Search name or phone..."
          value={filters.nameOrPhone || filters.name}
          onChange={(e) =>
            handleFilterChange("nameOrPhone", e.target.value)
          }
        />
        <input
          type="text"
          className="search-filter-input"
          placeholder="Search phone..."
          value={filters.phone}
          onChange={(e) => handleFilterChange("phone", e.target.value)}
        />
        <select
          className="search-filter-input search-filter-select"
          value={filters.city || ""}
          onChange={(e) => handleFilterChange("city", e.target.value)}
        >
          {/* Expect first option to act as "All Locations" */}
          <option value="">All Locations</option>
          {Array.from(
            new Set(
              allCities
                .map((c) => c.city_name || c.City_Name)
                .filter(Boolean)
            )
          )
            .sort()
            .map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
        </select>
        <select
          className="search-filter-input search-filter-select"
          value={
            filters.service === "All Services" || !filters.service
              ? ""
              : filters.service
          }
          onChange={(e) =>
            handleFilterChange(
              "service",
              e.target.value === "" ? "All Services" : e.target.value
            )
          }
        >
          {services.map((s) => (
            <option
              key={s}
              value={s === "All Services" ? "" : s}
            >
              {s}
            </option>
          ))}
        </select>
        <select
          className="search-filter-input search-filter-select"
          value={filters.domain || ""}
          onChange={(e) => handleFilterChange("domain", e.target.value)}
        >
          <option value="">All Businesses</option>
          {domains
            .map((d) => d.name)
            .sort()
            .map((dom) => (
              <option key={dom} value={dom}>
                {dom}
              </option>
            ))}
        </select>
        <select
          className="search-filter-input search-filter-select"
          value={filters.status || ""}
          onChange={(e) => handleFilterChange("status", e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="Interested">Interested</option>
          <option value="Followup">Followup</option>
          <option value="Not Interested">Not Interested</option>
          {/* extend with actual statuses list if needed */}
        </select>
        <button
          type="button"
          className="btn-search"
          onClick={handleSearchClick}
        >
          {isSearching ? "Searching..." : "Search"}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={handleClearFilters}
        >
          Clear
        </button>
      </div>

      {/* Free search bar */}
      <div className="free-search-bar search-in-actions-row">
        <input
          type="text"
          className="free-search-input"
          placeholder="Search..."
          value={filters.freeSearch}
          onChange={(e) => handleFilterChange("freeSearch", e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <div className="table-container">
          <table className="data-table leads-table-frozen">
            <tbody>
              <BusinessLeadsTable
                rows={currentLeads}
                servicesData={servicesData}
                domains={domains}
                getColumnWidth={() => undefined}
                getColumnMinWidth={() => undefined}
                handleView={onViewLead}
                handleEdit={onEditLead}
                handleDelete={() => {}}
              />
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination (local to business leads) */}
      {showPagination && (
        <div className="pagination">
          <button
            className="pagination-btn pagination-arrow"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            {"<"}
          </button>
          <div className="page-numbers">
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span
                  key={`ellipsis-${index}`}
                  className="page-number ellipsis"
                >
                  ...
                </span>
              ) : (
                <span
                  key={page}
                  className={`page-number ${
                    currentPage === page ? "active" : ""
                  }`}
                  onClick={() => handlePageClick(page)}
                >
                  {page}
                </span>
              )
            )}
          </div>
          <button
            className="pagination-btn pagination-arrow"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            {">"}
          </button>
          {totalRows > 0 && (
            <div className="pagination-info">
              {startRow}-{endRow} of {totalRows} items
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BusinessLeadsPage;

